/**
 * Google Analytics 4 (gtag) with Consent Mode v2.
 * gtag.js loads when VITE_GA_MEASUREMENT_ID is set; hits fire only after user accepts the banner.
 */
import { isTagDebugSession } from './tagDebug';

const MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)?.trim();
const CONSENT_STORAGE_KEY = 'explowords-analytics-consent';

export type AnalyticsConsent = 'granted' | 'denied';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let gtagShellReady = false;
let pendingPagePath: string | null = null;
let pendingPageTitle: string | null = null;

function hasGtag(): boolean {
  return typeof window.gtag === 'function';
}

export function isAnalyticsEnabled(): boolean {
  return Boolean(MEASUREMENT_ID);
}

export function getAnalyticsConsent(): AnalyticsConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (raw === 'granted' || raw === 'denied') return raw;
  } catch {
    // ignore
  }
  return null;
}

/** Load gtag.js with Consent Mode default denied (Tag Assistant can detect the tag). */
export function ensureGtagShell(): void {
  if (!MEASUREMENT_ID || gtagShellReady) return;

  window.dataLayer = window.dataLayer ?? [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
    window.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500,
    });
    window.gtag('js', new Date());
    const debug = isTagDebugSession();
    window.gtag('config', MEASUREMENT_ID, {
      send_page_view: true,
      anonymize_ip: true,
      ...(debug ? { debug_mode: true } : {}),
    });
  }

  const alreadyLoaded = document.querySelector(
    `script[src*="googletagmanager.com/gtag/js"]`
  );
  if (!alreadyLoaded) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
    document.head.appendChild(script);
  } else if (isTagDebugSession()) {
    window.gtag?.('config', MEASUREMENT_ID, { debug_mode: true });
  }

  gtagShellReady = true;
}

/** Tag Assistant session: grant storage + debug_mode so tagassistant.google.com can connect. */
function activateTagAssistantDebug(): void {
  if (!MEASUREMENT_ID) return;
  ensureGtagShell();
  window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
  window.gtag?.('config', MEASUREMENT_ID, {
    send_page_view: true,
    anonymize_ip: true,
    debug_mode: true,
  });
  if (pendingPagePath && pendingPageTitle) {
    trackPageView(pendingPagePath, pendingPageTitle);
  }
}

function activateAnalytics(): void {
  if (!MEASUREMENT_ID) return;
  ensureGtagShell();
  window.gtag?.('consent', 'update', {
    analytics_storage: 'granted',
  });
  window.gtag?.('config', MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
    ...(isTagDebugSession() ? { debug_mode: true } : {}),
  });
  if (pendingPagePath && pendingPageTitle) {
    trackPageView(pendingPagePath, pendingPageTitle);
  }
}

/** Store consent and enable analytics storage when granted. */
export function setAnalyticsConsent(granted: boolean): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, granted ? 'granted' : 'denied');
  } catch {
    // ignore
  }
  if (granted) {
    activateAnalytics();
  } else {
    ensureGtagShell();
    window.gtag?.('consent', 'update', {
      analytics_storage: 'denied',
    });
  }
}

/**
 * Register page context; loads gtag shell immediately if analytics is configured.
 * Events and page views run only after consent is granted.
 */
export function initAnalyticsWithConsent(pagePath: string, pageTitle: string): void {
  if (!MEASUREMENT_ID) return;
  pendingPagePath = pagePath;
  pendingPageTitle = pageTitle;
  ensureGtagShell();
  if (isTagDebugSession()) {
    activateTagAssistantDebug();
  } else if (getAnalyticsConsent() === 'granted') {
    activateAnalytics();
  }
}

export function trackPageView(pagePath: string, pageTitle: string): void {
  if (!hasGtag()) return;
  window.gtag?.('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: typeof location !== 'undefined' ? location.href : pagePath,
  });
}

export function trackEvent(
  name: string,
  params?: Record<string, string | number | boolean | undefined>
): void {
  if (!hasGtag()) return;
  const clean: Record<string, string | number | boolean> = {};
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) clean[k] = v;
    }
  }
  window.gtag?.('event', name, clean);
}
